/**
 * todo - resolve typing issues in another story
 */
import { defineStore, storeToRefs } from 'pinia';
import { computed, nextTick, ref } from 'vue';
import { Cookie } from '@vue-util/cookie';
import { cookies } from 'config';
import usePageDataStore from '~/stores/usePageData';
import { useEnvProps } from '~/composables/useEnvProps';
import { post } from '~/utils/api';
import { getUserIds } from '~/utils/cookiesUtils';
import { bvApiFieldsNamesMapper, FIELD_TYPES } from '~/modules/reviews/components/writeReview/constants';

/**
 * This store handles the entire process of writing and submitting a new review.
 * It manages form state, submission status, and API communication for posting a review.
 */
const useWriteReviewStore = defineStore('writeReviewStore', () => {
  const { pageData } = storeToRefs(usePageDataStore());
  const { hostlink } = useEnvProps();
  const { secureUserToken, userId } = getUserIds();

  // State
  const isSubmittingReview = ref<boolean>(false);
  const showSubmittingError = ref<boolean>(false);
  const isReviewedOnClient = ref<boolean>(false);

  // Getters
  const isCurrentProductReviewed = computed<boolean>(() => {
    const isReviewedOnSSR = pageData.value?.reviewsData?.isProductBeenReviewed;
    return isReviewedOnSSR || isReviewedOnClient.value;
  });

  // Actions
  /** Sets the flag indicating if a review is being submitted. */
  function setIsSubmittingReview(value: boolean) {
    isSubmittingReview.value = value;
  }

  /** Sets the flag to show a review submission error. */
  function setShowSubmittingError(value: boolean) {
    showSubmittingError.value = value;
  }

  /** Sets the flag indicating if the current product has been reviewed by the user on the client. */
  function setIsCurrentProductReviewed(value: boolean) {
    isReviewedOnClient.value = value;
  }

  /**
   * Constructs review attributes for the submission payload, such as campaign ID and photo URLs.
   * @param {Record<string, any> & { url: string}[]} photos
   * @param {Record<string, any> & { url: string} | null} video
   * @returns {{attributeName: string, attributeValue: any}[]}
   */
  function getReviewSubmitAttributes(photos, video = null) {
    const reviewAttributes = [];

    if (pageData.value?.campaignId) {
      reviewAttributes.push({
        attributeName: 'CampaignID',
        attributeValue: pageData.value.campaignId,
      });
    }

    photos.forEach(({ url }, index) => {
      reviewAttributes.push({
        attributeName: `photourl_${index + 1}`,
        attributeValue: url,
      });
    });

    if (video?.url) {
      reviewAttributes.push({
        attributeName: `videourl_1`,
        attributeValue: video.url,
      });
    }

    return reviewAttributes;
  }

  /**
   * Processes the 'extra questions' part of the review form, transforming the data
   * into the flat key-value structure required by the Bazaarvoice API.
   * It handles different question types like 'scale', 'tiles', and 'size'.
   * @param {Array<Record<string, any> & {type: string; name: string}>} extraQuestions
   * @returns {Record<string, any>}
   */
  function processExtraQuestionsValues(extraQuestions) {
    return extraQuestions.reduce((result, question) => {
      const { type, value, name } = question;
      switch (type) {
        case FIELD_TYPES.scale:
          return {
            ...result,
            [bvApiFieldsNamesMapper[name]]: value ? Number(value) : null,
          };
        case FIELD_TYPES.tiles:
          return {
            ...result,
            ...value.reduce((acc, tileName) => ({ ...acc, [tileName.replace('-', '/')]: true }), {}),
          };
        case FIELD_TYPES.size:
          return {
            ...result,
            ...Object.keys(value).reduce((acc, sizeName) => ({ ...acc, [bvApiFieldsNamesMapper[sizeName]]: value[sizeName] }), {}),
          };
        default:
          return result;
      }
    }, {});
  }
  /**
   * Sets a cookie to mark a product as reviewed by the current user.
   * @param {string} id - The product ID.
   */
  function setProductAsReviewed(id) {
    const productsReviewed = JSON.parse(Cookie.get(cookies.productsReviewed) || '{}');
    productsReviewed[id] = 1;
    Cookie.set(cookies.productsReviewed, JSON.stringify(productsReviewed));
  }
  /**
   * Sets a cookie with the username from the last submitted review.
   * @param {string} name
   */
  function setLastReviewUsername(name) {
    Cookie.set(cookies.lastReviewUsername, name);
  }

  function getLastReviewUsername() {
    return Cookie.get(cookies.lastReviewUsername);
  }

  /**
   * Constructs the review payload and posts it to the Bazaarvoice API.
   * Handles the full submission lifecycle including loading states, error handling, and success side effects.
   * @param {WriteReviewObject} reviewObject - The complete review data from the form.
   * @param {string|null} [forcedProductId=null] - An optional product ID to override the default.
   * @returns {Promise<void>}
   */
  async function submitReview(reviewObject, forcedProductId = null) {
    const { rating, reviewTitle, reviewText, nickname, email, doRecommend, sweepstakes, photos, video, extraQuestions } = reviewObject;
    const submissionFields = {
      rating: String(rating),
      agreedtotermsandconditions: true,
      title: reviewTitle.trim(),
      reviewtext: reviewText.trim(),
      usernickname: nickname.trim(),
      email: email.trim(),
      contextdatavalue_IncentivizedReview: sweepstakes,
      isrecommended: false,
      ...processExtraQuestionsValues(extraQuestions),
    };

    if (typeof doRecommend === 'boolean') {
      submissionFields.isrecommended = doRecommend;
    }

    const data = {
      progressiveSubmit: {
        locale: 'en_US',
        userId,
        productId: String(forcedProductId || pageData.value.productId),
        submissionFields,
        reviewSubmitAttributes: getReviewSubmitAttributes(photos, video),
      },
    };
    setIsSubmittingReview(true);

    try {
      const { PersistentLoginUtil } = await import(/* webpackChunkName: "persistent-login", webpackPrefetch: true */ '@vue-util/persistentLogin');
      const res = await post(`${hostlink}/xapi/digital/v1/product/progressivereview?secureUserToken=${secureUserToken}`, data, {
        headers: PersistentLoginUtil.getDeviceFingerPrintHeaders(),
      });
      if (typeof res === 'string') throw new Error('Unknown error');
      else {
        setLastReviewUsername(nickname.trim());
        setProductAsReviewed(pageData.value.productId);
        setIsCurrentProductReviewed(true);
      }
    } catch (e) {
      console.error(e);
      setShowSubmittingError(true);
      await nextTick(() => {
        window.scrollTo(0, document.body.scrollHeight);
      });
    } finally {
      setIsSubmittingReview(false);
    }
  }
  /**
   * @param {string} id
   * @returns {boolean}
   */
  function isProductBeenReviewed(id) {
    const productsReviewed = JSON.parse(Cookie.get(cookies.productsReviewed) || '{}');
    return productsReviewed ? !!productsReviewed[id] : false;
  }

  function updateIsCurrentProductReviewed() {
    setIsCurrentProductReviewed(isProductBeenReviewed(pageData.value.productId));
  }

  return {
    // State
    showSubmittingError,
    isSubmittingReview,
    isReviewedOnClient,
    // Getters
    isCurrentProductReviewed,
    // Actions
    submitReview,
    getReviewSubmitAttributes,
    processExtraQuestionsValues,
    updateIsCurrentProductReviewed,
    isProductBeenReviewed,
    getLastReviewUsername,
  };
});

export default useWriteReviewStore;
